import React, { ComponentProps, ReactElement } from 'react'
import CartReviewTileBase from './_CartReviewTileBase'
import CartReviewTilePrimaryContent from './_CartReviewTilePrimaryContent'
import CartReviewTileSecondaryContent from './_CartReviewTileSecondaryContent'
import LoadingCartReviewTileSkeleton from './LoadingCartReviewTileSkeleton'
import CartReviewTileImage from './_CartReviewTileImage'
import DeletedCartReviewTile from './DeletedCartReviewTile'
import CondensedCartReviewTile from './CondensedCartReviewTile'

type InheritedCartReviewTileBaseProps = Pick<ComponentProps<typeof CartReviewTileBase>, 'to' | 'banner' | 'extension'>
type InheritedCartReviewTilePrimaryContentProps = Pick<ComponentProps<typeof CartReviewTilePrimaryContent>, 'title' |'dates' | 'packageTitle' | 'metaData' | 'occupancy' | 'cancellationPolicy'>
type InheritedCartReviewTileSecondaryContentProps = Pick<ComponentProps<typeof CartReviewTileSecondaryContent>, 'price'>
type InheritedCartReviewTileImageProps = Pick<ComponentProps<typeof CartReviewTileImage>, 'label'>

export interface LoyaltyPointsEarningConfiguration {
  calculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined>,
  calculationType: 'estimate' | 'exact'
  hideStatusCredits?: boolean
}

interface Props extends InheritedCartReviewTileBaseProps, InheritedCartReviewTilePrimaryContentProps, InheritedCartReviewTileSecondaryContentProps, InheritedCartReviewTileImageProps {
  itemId: string
  imageElement?: ReactElement
  fetching?: boolean
  isBookable: boolean
  saveForLater: boolean
  deleted: boolean
  onSaveForLater: (e: React.MouseEvent<HTMLButtonElement>) => void
  onDelete: (e: React.MouseEvent<HTMLButtonElement>) => void
  loyaltyPointsEarningConfiguration?: LoyaltyPointsEarningConfiguration,
  className?: string
  hideAvailabilityDetails?: boolean
  hideTitleAndDates?: boolean
  disableOverlay?: boolean
  roomName?: string
  tileVariant?: 'default' | 'condensed'
  comparisonCheckbox?: ReactElement
}

function CartReviewTile({
  itemId,
  fetching,
  label,
  imageElement,
  title,
  dates,
  packageTitle,
  metaData,
  occupancy,
  cancellationPolicy,
  price,
  banner,
  extension,
  to,
  isBookable,
  saveForLater,
  deleted,
  loyaltyPointsEarningConfiguration,
  onSaveForLater,
  onDelete,
  className,
  hideAvailabilityDetails = false,
  hideTitleAndDates = false,
  disableOverlay = false,
  roomName,
  tileVariant = 'default',
  comparisonCheckbox,
}: Props) {
  if (fetching) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (deleted) {
    return <DeletedCartReviewTile title={title} itemIds={[itemId]} />
  }

  if (tileVariant === 'condensed') {
    return (
      <CondensedCartReviewTile
        to={to}
        banner={banner}
        imageElement={imageElement}
        label={label}
        title={title}
        dates={dates}
        packageTitle={packageTitle}
        metaData={metaData}
        occupancy={occupancy}
        price={price}
        roomName={roomName}
        hideTitleAndDates={hideTitleAndDates}
        onDelete={onDelete}
      />
    )
  }

  return (
    <CartReviewTileBase
      banner={banner}
      extension={extension}
      to={to}
      layout={imageElement ? 'with-image' : 'without-image'}
      showOverlay={disableOverlay ? false : !isBookable || saveForLater}
      className={className}
      hideAvailabilityDetails={hideAvailabilityDetails}
    >
      {imageElement && <CartReviewTileImage
        imageElement={imageElement}
        label={label}
      />}
      <CartReviewTilePrimaryContent
        title={title}
        dates={dates}
        packageTitle={packageTitle}
        metaData={metaData}
        occupancy={occupancy}
        cancellationPolicy={cancellationPolicy}
        label={label}
        alwaysDisplayLabel={!imageElement}
        loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
        hideAvailabilityDetails={hideAvailabilityDetails}
        hideTitleAndDates={hideTitleAndDates}
        roomName={roomName}
        comparisonCheckbox={comparisonCheckbox}
      />
      {!hideAvailabilityDetails && <CartReviewTileSecondaryContent
        price={price}
        isBookable={isBookable}
        saveForLater={saveForLater}
        deleted={deleted}
        onSaveForLater={onSaveForLater}
        onDelete={onDelete}
      />}
    </CartReviewTileBase>
  )
}

export default CartReviewTile
